import {
  FacetConfig,
  FacetResponses,
  FacetState,
  FacetRootStore,
  FacetStore,
  LookupData,
  LookupSourceIdentifier,
  MediaSourceId,
  MediaSourceData,
} from "@globalgiving/vast-frontend-common"
import { action, comparer, computed, observable } from "mobx"
import { Category } from "~/stores/domain/objects"
import { CategoryStore, InvalidFacetInfo } from "~/types"

export class EntityTaskStore implements FacetRootStore {
  @observable accessor categories: CategoryStore[] = []
  @observable accessor programRelevantFacets: string[] = []
  @observable accessor lookupSources: Map<LookupSourceIdentifier, LookupData | null> = new Map()
  @observable accessor media: Map<MediaSourceId, MediaSourceData | null> = new Map()

  @computed
  get facets(): FacetStore[] {
    return this.categories.flatMap((category) => category.facets)
  }

  /**
   * @returns the first facet that failed to construct, if any.
   */
  @computed
  get invalidFacet(): InvalidFacetInfo | null {
    return this.categories.find((category) => category.invalidFacet)?.invalidFacet ?? null
  }

  @computed
  get hasUnsavedChanges(): boolean {
    return this.facets.some((facet) => facet.dirty)
  }

  /**
   * @returns categories that have facet in INPUT state
   */
  @computed
  get inputCategories(): CategoryStore[] {
    return this.categories
      .map((category) => {
        const filteredCategory = { ...category }
        filteredCategory.facets = category.facets.filter((facet) => facet.config.state === FacetState.Input)

        return filteredCategory
      })
      .filter((category) => !!category.facets.length)
  }

  /**
   * @returns categories that have facets in INPUT state and the facets are relevant
   */
  @computed
  get activeTasks(): CategoryStore[] {
    return this.inputCategories
      .map((category) => {
        const filteredCategory = { ...category }
        filteredCategory.facets = category.facets.filter((facet) =>
          this.programRelevantFacets.includes(facet.config.name)
        )

        return filteredCategory
      })
      .filter((category) => !!category.facets.length)
  }

  /**
   * @returns categories that have facet in APPROVED or REJECTED state
   */
  @computed
  get completedCategories(): CategoryStore[] {
    return this.categories
      .map((category) => {
        const filteredCategory = { ...category }
        filteredCategory.facets = category.facets.filter((facet) =>
          [FacetState.Approved, FacetState.Rejected].includes(facet.config.state)
        )

        return filteredCategory
      })
      .filter((category) => !!category.facets.length)
  }

  @action
  update(facetConfigs: FacetConfig[]): void {
    this.categories = this.sortByCategory(facetConfigs)
  }

  @action
  updateValues(responses: FacetResponses): void {
    Object.entries(responses).forEach(([key, values]) => {
      const targetFacet = this.facets.find((facet) => facet.config.name === key)
      if (targetFacet) {
        targetFacet.updateValues(values)
      }
    })
  }

  @action
  updateLookupSource(lookupSourceIdentifier: LookupSourceIdentifier, data: LookupData | null): void {
    this.lookupSources.set(lookupSourceIdentifier, data)
  }

  @action
  updateMedia(id: MediaSourceId, data: MediaSourceData | null): void {
    /* Skip the write when nothing actually changed.  */
    if (comparer.structural(this.media.get(id), data)) {
      return
    }

    this.media.set(id, data)
  }

  /**
   * Groups the given facets by their `category_path` and creates a {@link Category}
   * instance for each distinct category.
   *
   * Each group of facets with the same `category_path` is converted into a
   * `Category` using the category path as the name (unique identifier) and the first facet's
   * `category` value as the label fallback.
   *
   * @param facets - The list of facet configurations to group into categories.
   * @returns An array of {@link Category} instances, one for each category path.
   */
  sortByCategory(facets: FacetConfig[]): Category[] {
    const categoryFacets = new Map<string, FacetConfig[]>()
    facets.forEach((facet) => {
      const category = categoryFacets.get(facet.category_path)
      if (category) {
        category.push(facet)
      } else {
        categoryFacets.set(facet.category_path, [facet])
      }
    })

    return Array.from(categoryFacets.entries()).map(([categoryPath, configs]) => {
      // All the facets inside one category have the same category name
      const categoryName = configs[0].category || categoryPath
      const categoryConfig = {
        categoryName: categoryPath,
        categoryLabel: categoryName,
        facets: configs,
      }

      return new Category(categoryConfig, this)
    })
  }

  @action
  setProgramRelevantFacets(facetNames: string[]): void {
    this.programRelevantFacets = facetNames
  }
}

export const entityTaskStore = new EntityTaskStore()
